import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button,
  Card, 
  CardContent,
  Grid,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  School as SchoolIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { API_URL, createAxiosInstance } from '../../../api';
import EnhancedCourseCard from './EnhancedCourseCard';

const MyCoursesTab = React.memo(() => {
  const theme = useTheme();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.main.userId);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Handle Add Course navigation
  const handleAddCourse = () => {
    navigate('/course/new');
  };

  // Fetch courses
  const fetchCourses = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(`${API_URL}/api/courses/coursesUser/${userId}/false`);
      
      setCourses(response.data || []);
      
    } catch (error) {
      console.error('Error fetching courses:', error);
      // Only show error for actual API failures, not for empty results
      if (error.response?.status >= 500) {
        setError('Failed to load courses. Please try again.');
      } else {
        // For client errors (400-499), still set empty array
        setCourses([]);
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        height: '400px',
        gap: 2
      }}>
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Loading your courses...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3 
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Your Courses ({courses.length})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and monitor your course listings
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          onClick={handleAddCourse}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            borderRadius: 2,
            px: 3,
            py: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: `0 4px 16px ${theme.palette.primary.main}30`,
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: `0 6px 20px ${theme.palette.primary.main}40`,
              transform: 'translateY(-1px)'
            }
          }}
        >
          + Add Course
        </Button>
      </Box>

      {/* Courses Grid */}
      {courses.length > 0 ? (
        <Grid container spacing={2.5}>
          {courses.map((course) => (
            <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={course._id || course.id}>
              <EnhancedCourseCard 
                course={course} 
                userId={userId}
                onDelete={fetchCourses}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Card sx={{ 
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          border: '2px dashed #e2e8f0'
        }}>
          <CardContent>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              py: 6,
              gap: 3
            }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <SchoolIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
              </Box>
              
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                  No Courses Yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, textAlign: 'center' }}>
                  Start building your teaching portfolio by creating your first course using the "Add Course" button above.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
});

MyCoursesTab.displayName = 'MyCoursesTab';

export default MyCoursesTab;
